/**
 * A ticket tracker's credential shared by an organization (§5.21, §5.25): Jira's
 * account email and API token, or Trello's API key and token, kept as the
 * organization's own secrets under reserved names — sealed end to end on an
 * owner's or an admin's machine, handed sealed to the machines they reach
 * (`org-secrets.ts`), opened there.
 *
 * Reserved names rather than a typed entry of their own: they are organization
 * secrets in every respect that matters — who receives them, who set them and
 * when, the audit, the rotation — and a second kind of sealed value would be a
 * second copy of all of that.
 *
 * **Not an environment variable.** A name here is never put in a session's
 * environment: the task sync runs in the main process (§5.21) and a tracker's
 * credential never enters a sandbox, the same rule as a person's own.
 *
 * **The member's own wins.** A machine with a credential of its own for the
 * site uses it; the organization's is the fallback for a member with none.
 *
 * **Jira's names its site, sealed with it.** Jira's credential is used only on
 * the site sealed beside it under a third reserved name (`AIRLOCK_JIRA_SITE`),
 * by the same owner's or admin's machine, under the same organization key — so
 * the account area, which relays every setting and holds no key, cannot point
 * it at a host of its choosing (security review finding SEC-06). The
 * organization's settings (`jiraHost` in its onboarding) are that relayed word
 * and decide nothing here. A Jira credential shared before the site was sealed
 * with it names no site and is not used until it is shared again.
 */

import { jiraHost } from '../settings/primitives';

/** The reserved organization-secret names, by provider and part. */
export const SHARED_INTEGRATION_SECRETS = {
  jira: { account: 'AIRLOCK_JIRA_EMAIL', token: 'AIRLOCK_JIRA_TOKEN', site: 'AIRLOCK_JIRA_SITE' },
  trello: { account: 'AIRLOCK_TRELLO_KEY', token: 'AIRLOCK_TRELLO_TOKEN' }
} as const;

export type SharedIntegrationKind = keyof typeof SHARED_INTEGRATION_SECRETS;

const RESERVED: readonly string[] = Object.values(SHARED_INTEGRATION_SECRETS).flatMap((one): string[] =>
  Object.values(one)
);

/** Whether an organization secret is a tracker's credential — never handed to a session's environment. */
export function isIntegrationSecret(name: string): boolean {
  return RESERVED.includes(name);
}

/**
 * Both halves of one provider's shared credential, from a list of opened
 * secrets, or null when either is missing — with, for Jira, the site sealed
 * beside it as a bare host (`jiraHost`), or null when none was or it is not one.
 */
export function sharedCredentialIn(
  kind: SharedIntegrationKind,
  secrets: readonly { name: string; value: string }[]
): { account: string; token: string; site: string | null } | null {
  const names = SHARED_INTEGRATION_SECRETS[kind];
  const account = secrets.find((one) => one.name === names.account)?.value.trim();
  const token = secrets.find((one) => one.name === names.token)?.value.trim();
  const sealed = 'site' in names ? secrets.find((one) => one.name === names.site)?.value : undefined;
  const site = sealed === undefined ? null : jiraHost(sealed) || null;
  return account && token ? { account, token, site } : null;
}
